import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { SavedView } from 'models/saved_view'
import { cn } from 'components/UI/cn'
import { Dialog, DialogContent, DialogClose } from 'components/UI/Modal'
import FbButton from 'components/FbUI/FbButton'
import Checkbox from 'components/Checkbox/checkbox'
import { PiXBold } from 'react-icons/pi'

const savedFilterFormSchema = z.object({
  name: z
    .string()
    .min(3, 'Must be at least 3 characters')
    .max(50, 'Must be 50 characters or less'),
  shared: z.boolean(),
})

export type SavedFilterFormData = z.infer<typeof savedFilterFormSchema>

export interface SavedFilterModalProps {
  show: boolean
  handleClose: () => void
  onSubmitSuccess?: () => void
  submitApiAction: (values: SavedFilterFormData) => Promise<unknown>
  savedFilterToEdit?: SavedView | null
  confirmButtonText?: string
  loading?: boolean
  title?: string
  description?: string
}

export function SavedFilterModal({
  show,
  handleClose,
  onSubmitSuccess,
  submitApiAction,
  savedFilterToEdit,
  confirmButtonText = 'Save',
  loading = false,
  title = 'Save Filter',
  description = 'Create a new filter to save this view, which includes both filters and any search terms.',
}: SavedFilterModalProps) {
  const defaultValues = useMemo((): SavedFilterFormData => {
    if (!savedFilterToEdit) {
      return {
        name: '',
        shared: true,
      }
    }

    return {
      name: savedFilterToEdit.name,
      shared: savedFilterToEdit.shared,
    }
  }, [savedFilterToEdit])

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, isValid, errors },
    setError,
    reset,
    watch,
    setValue,
  } = useForm<SavedFilterFormData>({
    resolver: zodResolver(savedFilterFormSchema),
    defaultValues,
    mode: 'onChange',
  })

  // Reset form when savedFilterToEdit changes
  useEffect(() => {
    if (savedFilterToEdit) {
      reset({
        name: savedFilterToEdit.name,
        shared: savedFilterToEdit.shared,
      })
    } else {
      reset({
        name: '',
        shared: true,
      })
    }
  }, [savedFilterToEdit, reset])

  const _handleClose = () => {
    reset()
    handleClose()
  }

  const onSubmit = handleSubmit(async (data) => {
    try {
      await submitApiAction(data)
      reset()
      onSubmitSuccess?.()
      _handleClose()
    } catch (e: any) {
      if (e.response?.data?.error) {
        const errorMsg = e.response.data.error
        setError('name', { message: errorMsg })
      } else if (e.response?.data?.name) {
        setError('name', { message: e.response.data.name[0] })
      } else {
        setError('name', { message: `Error saving filter: ${e.message}` })
      }
    }
  })

  const isLoading = isSubmitting || loading
  const sharedChecked = watch('shared')

  return (
    <Dialog open={show} onOpenChange={(open) => !open && _handleClose()}>
      <DialogContent
        size="md"
        noPadding
        disableCloseIcon
        className="!gap-0 !rounded-xl !py-0"
      >
        <form
          onSubmit={onSubmit}
          className="flex flex-col flex-1 min-h-0 overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-start justify-between gap-4 px-6 pb-3 pt-6">
            <div className="flex flex-col gap-1">
              <h2 className="m-0 text-2xl font-semibold leading-8 tracking-[-0.2px] text-neutral-800">
                {title}
              </h2>
              {description && (
                <p className="m-0 text-sm text-neutral-500 leading-5">
                  {description}
                </p>
              )}
            </div>
            <DialogClose asChild>
              <button
                type="button"
                aria-label="Close"
                className="flex items-center justify-center rounded-lg p-2.5 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-500 flex-shrink-0"
              >
                <PiXBold size={20} />
              </button>
            </DialogClose>
          </div>

          {/* Body */}
          <div className="flex-1 flex flex-col gap-4 px-6 pb-4 pt-2">
            <div className="flex flex-col w-full gap-1.5">
              <label
                htmlFor="saved-filter-name"
                className="text-sm font-medium text-neutral-700 select-none"
              >
                Filter name
              </label>
              <input
                {...register('name')}
                id="saved-filter-name"
                type="text"
                placeholder="Hint: make names descriptive to help find them later!"
                className={cn(
                  'w-full h-9 px-3 rounded-lg text-sm text-neutral-800 placeholder:text-neutral-400',
                  'bg-white transition-shadow',
                  'focus:outline-none focus-visible:outline-none',
                  errors.name
                    ? 'border border-error-strong shadow-[0_0_0_4px_rgba(220,38,38,0.2)]'
                    : 'border border-neutral-300 hover:border-neutral-400 focus:border-brand-active focus:shadow-[0_0_0_4px_rgba(8,116,67,0.2)]'
                )}
                disabled={isLoading}
              />
              {errors.name && (
                <p className="m-0 text-xs leading-4 text-error-strong">
                  {errors.name.message}
                </p>
              )}
            </div>

            <label
              htmlFor="saved-filter-shared"
              className="flex items-center gap-2 cursor-pointer select-none"
            >
              <Checkbox
                id="saved-filter-shared"
                checked={sharedChecked}
                onChange={(e) => setValue('shared', e.target.checked)}
                disabled={isLoading}
                wrapperClassName="flex-shrink-0"
              />
              <span className="text-sm text-neutral-700">
                Share filter with team
              </span>
            </label>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 border-t border-line px-6 py-4">
            <DialogClose asChild>
              <FbButton variant="secondary" type="button" disabled={isLoading}>
                Cancel
              </FbButton>
            </DialogClose>
            <FbButton
              type="submit"
              loading={isLoading}
              disabled={!isValid || isLoading}
              className="!bg-brand-bright !text-white hover:!bg-brand-active active:!bg-brand-active disabled:!bg-neutral-100 disabled:!text-neutral-400 disabled:!border-neutral-100 disabled:hover:!bg-neutral-100"
            >
              {confirmButtonText}
            </FbButton>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
